/**
 * VISTOERA_SAAS_I18N_FOUNDATION V1 — canonical date/time/number formatting
 * helpers (spec §10/§11). Built on `Intl.DateTimeFormat`/`Intl.NumberFormat`
 * directly (not dayjs's own `.format()` templates) so UI display formatting
 * follows locale-correct field order automatically instead of a hardcoded
 * `'YYYY年MM月DD日'` / `'MM/DD/YYYY'` template per locale (spec §10 forbids
 * both). dayjs (via `antdLocale.ts::syncDayjsLocale`) still drives AntD's
 * own built-in components (DatePicker calendar labels etc.) — this module
 * is for Vistoera's OWN business UI copy (timestamps on cards, tables...).
 *
 * Never format a value that is about to be sent back to the API/DB with
 * these — they are presentation-only (spec §10 "不要因为 UI locale 修改数据库
 * 时间格式").
 */
import type { UiLocale } from './types'

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value)
}

export function formatDate(value: string | number | Date, locale: UiLocale): string {
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    toDate(value),
  )
}

export function formatDateTime(value: string | number | Date, locale: UiLocale): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value))
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
]

export function formatRelativeTime(value: string | number | Date, locale: UiLocale, now: Date = new Date()): string {
  const diffSeconds = (toDate(value).getTime() - now.getTime()) / 1000
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (Math.abs(diffSeconds) >= secondsInUnit) {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit)
    }
  }
  return rtf.format(Math.round(diffSeconds), 'second')
}

export function formatNumber(value: number, locale: UiLocale, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, options).format(value)
}
